// Featured reports surfaced as widgets on the dashboard.
//
// Match by `code` (preferred — stable across environments) or numeric `id`.
// Entries that don't resolve to an active report are silently skipped.
//
// When the list is empty, the dashboard falls back to the first few reports
// returned by the API so the page still has content out of the box. Replace
// with explicit codes/ids once the report set stabilises.
//
// The shortcut codes must stay in sync with WIDGET_VALUE_SOURCES below: a
// shortcut without an entry there renders as an empty card, since no value is
// ever fetched for it.
export const FEATURED_REPORTS = {
  shortcuts: [
    'Programari-tip_pacienti',
    'Estimare_venituri-servicii_programate',
    'Receptie_programari',
    'Receptie_apeluri',
  ],
};

export const DEFAULT_SHORTCUT_COUNT = 4;

// Per-widget hard-coded value override. Keyed by report `code`. Wins over
// any value fetched dynamically. Use this only for static numbers (e.g. demo
// data) — for real numbers driven by a report, use WIDGET_VALUE_SOURCES.
//
// Shape: { value: number, unit?: string, caption?: string }
export const WIDGET_VALUES = {};

// Per-widget dynamic value definition. Keyed by report `code`. When set,
// the dashboard fetches /_seal/value/:reportId, which executes the source
// report via SEAL's standard flow (SWILogin → SWExecuteReport →
// ActionExecuteReport → poll until result_ready), then fetches the
// rendered JSON via /ViewResult?format=Json, and aggregates the requested
// `column` to a single number.
//
// Shape:
//   {
//     column: string,            // column key in the model's DataTable
//     aggregate: 'sum'|'count'|'max'|'min'|'avg'|'first'|'last',
//     modelName?: string,        // optional — disambiguate when the report
//                                // has multiple models, otherwise the
//                                // first model with a matching column wins
//     unit?: string,             // shown next to the number
//     caption?: string,          // shown under the number
//   }
//
// SEAL returns the report as an array. Element [0] is metadata
// ({ReportName, ExecutionDate, ExecutionDuration}); subsequent elements
// are `{ModelName, DataTable: [{col: val, …}, …]}` per model view. The
// parser pulls each DataTable as a {headers, rows, modelName} entry.
//
// Numeric strings are tolerated — both Romanian/EU "1.234,56" and English
// "1,234.56" parse correctly; trailing units like "RON" are stripped.
//
// Example:
//   'Programari-tip_pacienti': {
//     modelName: '01. KPI Programari',
//     column: 'Value',
//     aggregate: 'first',           // KPI tables typically expose one row
//     unit: 'pacienți',
//     caption: 'Pacienți noi',
//   },
export const WIDGET_VALUE_SOURCES = {
  // First-cut configs derived from the .srex element definitions. The
  // KPI model in each report is single-row with several Data columns;
  // `aggregate: 'first'` returns that single value. Column names use the
  // element DisplayName SEAL emits in the JSON DataTable — if these come
  // back empty, look at `debug.tablesFound` in the proxy response to see
  // the actual JSON keys and adjust.
  'Programari-tip_pacienti': {
    modelName: '01. KPI tiles',
    column: 'Pacienți Noi',
    aggregate: 'first',
    unit: 'pacienți',
    caption: 'Pacienți noi',
  },
  'Estimare_venituri-servicii_programate': {
    modelName: '01. KPI Programari',
    column: 'Venituri totale estimate din programări',
    aggregate: 'first',
    unit: 'RON',
    caption: 'Venituri estimate',
  },
  'Receptie_programari': {
    modelName: '01. KPI_Programari',
    column: 'Timp mediu livrare',
    aggregate: 'first',
    caption: 'Timp mediu livrare',
  },
  'Receptie_apeluri': {
    modelName: '01. ApeluriKPI',
    column: 'Apeluri',
    aggregate: 'first',
    unit: 'apel.',
    caption: 'Apeluri totale',
  },
};
